import { useEffect, useRef, useState } from "react";

type Slide = {
  eyebrow: string;
  head: string;
  accent: string;
  subhead: string;
  impact: string;
};

// Rotating hero. Each slide is one punchy beat of the canon:
// the reframe, the trust harness, the audit layer.
// `impact` = a sourced business-consequence line so a peer-CFO feels the cost
// at the moment of the insight, not only in the Problem stats block below.
const SLIDES: Slide[] = [
  {
    eyebrow: "The trust harness for an agentic world",
    head: "Systems are connected.",
    accent: "Meaning isn't.",
    subhead:
      "Integrations move data, not meaning. Meshly detects, explains, and reconciles where meaning breaks between your systems of record.",
    impact:
      "That gap costs finance teams four days a month, nearly 20% of the year.",
  },
  {
    eyebrow: "The AI verification layer",
    head: "Models evolve.",
    accent: "Trust doesn't.",
    subhead:
      "Meshly verifies every action, human or agent, back to its source. The model underneath is swappable; the trust harness is what lasts.",
    impact: "Only 10% of CFOs fully trust the data their AI runs on.",
  },
  {
    eyebrow: "Auditability and explainability",
    head: "Explain every number.",
    accent: "",
    subhead:
      "Meshly explains why a number is what it is and traces it to the decision that made it. When the auditor asks, you can show the work, whether it was done by a person or an agent.",
    impact: "Teams burn 15 to 20 hours a week proving numbers by hand.",
  },
];

const ROTATION_MS = 10000;
const TRANSITION_MS = 500;

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const reduceMotionRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reduceMotionRef.current = mq.matches;
    const onChange = (e: MediaQueryListEvent) => {
      reduceMotionRef.current = e.matches;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (reduceMotionRef.current || isPaused) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, ROTATION_MS);
    return () => window.clearInterval(id);
  }, [isPaused]);

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <div className="grid" aria-live="polite">
        {SLIDES.map((slide, i) => {
          // All slides stay mounted so the opacity crossfade works, so only slide 0
          // (the one SSG/first paint always shows) gets a real <h1> -- otherwise every
          // mounted slide adds its own <h1> and the page has three at once.
          const Heading = i === 0 ? "h1" : "p";
          return (
            <div
              key={i}
              className={`col-start-1 row-start-1 transition-opacity ease-in-out ${
                i === index ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              style={{ transitionDuration: `${TRANSITION_MS}ms` }}
              aria-hidden={i !== index}
            >
              <div className="space-y-6">
                <span className="inline-block font-mono-tech text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                  {slide.eyebrow}
                </span>
                <Heading className="font-display text-5xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[0.98] text-foreground">
                  {slide.head}
                  {slide.accent && (
                    <>
                      <br />
                      <span className="text-primary">{slide.accent}</span>
                    </>
                  )}
                </Heading>
                {/* subhead + sourced impact line are one tight unit (anchors the lower-left) */}
                <div className="space-y-3 max-w-[34rem]">
                  <p className="text-lg lg:text-xl text-foreground/75 leading-relaxed">
                    {slide.subhead}
                  </p>
                  <p className="text-base lg:text-lg italic font-medium text-primary leading-snug">
                    {slide.impact}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 mt-8" role="tablist" aria-label="Hero messages">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-8 bg-primary" : "w-1.5 bg-foreground/20 hover:bg-foreground/40"
            }`}
            aria-label={`Show message ${i + 1} of ${SLIDES.length}`}
            aria-current={i === index ? "true" : undefined}
          />
        ))}
      </div>
    </div>
  );
}
